import { makeStyles } from '@material-ui/core/styles';
import { ReactFlow, ReactFlowProvider } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { color } from '../../app/theme';
import { NodeType } from '../../app/types';
import errorHandle from '../../features/errorHandle';
import { checkPlatform } from '../../features/mainSlice';
import CircularProgress from '../shared/CircularProgress';
import AddEdge from './AddEdge';
import ConditionComponent from './Condition';
import Controls from './Controls';
import CouponComponent from './Coupon';
import CustomerConditionComponent from './CustomerCondition';
import EndComponent from './End';
import GAConditionComponent from './GACondition';
import GroupComponent from './Group';
import InboxComponent from './Inbox';
import MAComponent from './MA';
import MessageComponent from './Message';
import MiniMap from './MiniMap';
import Preview from './Preview';
import ScheduleComponent from './Schedule';
import SettingBar from './SettingBar';
import StartComponent from './Start';
import TagComponent from './Tag';
import TriggerComponent from './Trigger';
import UsageNum from './UsageNum';
import WaitComponent from './Wait';

const useStyles = makeStyles(
  {
    root: {
      width: '100%',
      height: '100%',
      padding: 44,
      position: 'relative',
    },
    loading: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      height: '100%',
    },
    flow: {
      background: color.$19,
      cursor: 'move',
    },
  },
  {
    name: 'mainContainer',
  },
);

const mainContainer = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [zoom, setZoom] = useState(null);
  const [instance, setInstance] = useState(null);
  const { data, isPreview, isLoading } = useSelector(store => store.ma);
  const { integration } = useSelector(store => store.organization);
  const { nodes, edges, platform } = data || {};

  const nodeTypes = useMemo(
    () => ({
      [NodeType.TRIGGER]: TriggerComponent,
      [NodeType.MESSAGE]: MessageComponent,
      [NodeType.CONDITION]: ConditionComponent,
      [NodeType.START]: StartComponent,
      [NodeType.END]: EndComponent,
      [NodeType.GROUP]: GroupComponent,
      [NodeType.MA]: MAComponent,
      [NodeType.TAGGING]: TagComponent,
      [NodeType.INBOX]: InboxComponent,
      [NodeType.GACONDITION]: GAConditionComponent,
      [NodeType.CUSTOMERCONDITION]: CustomerConditionComponent,
      [NodeType.COUPON]: CouponComponent,
      [NodeType.SCHEDULE]: ScheduleComponent,
      [NodeType.WAIT]: WaitComponent,
    }),
    [],
  );

  const edgeTypes = useMemo(
    () => ({
      add: AddEdge,
    }),
    [],
  );

  const onInit = reactFlowInstance => {
    setInstance(reactFlowInstance);
    reactFlowInstance.zoomTo(1);
  };

  const onMove = (event, res) => {
    setZoom(res?.zoom);
  };

  useEffect(() => {
    if (integration) {
      const isPlatformVerify = checkPlatform(platform, integration);

      if (!isPlatformVerify) {
        dispatch(errorHandle(t('platformError')));
      }
    }
  }, [integration]);

  return (
    <div className={classes.root}>
      {isLoading ? (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      ) : (
        <ReactFlowProvider>
          <ReactFlow
            className={classes.flow}
            nodes={nodes || []}
            edges={edges || []}
            onInit={onInit}
            onMove={onMove}
            nodesConnectable={false}
            nodesDraggable={false}
            zoomOnScroll
            panOnScroll={false}
            panOnScrollMode={false}
            zoomOnDoubleClick={false}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            selectionKeyCode="disabled"
            multiSelectionKeyCode="disabled"
            deleteKeyCode="disabled"
            minZoom={0.1}
            maxZoom={5}
            fitView
          >
            <Controls zoom={zoom} instance={instance} />
            <MiniMap
              nodeColor={node => {
                switch (node.type) {
                  case NodeType.END:
                    return color.$2;
                  case NodeType.CUSTOMERCONDITION:
                  case NodeType.GACONDITION:
                  case NodeType.CONDITION:
                    return color.$15;
                  case NodeType.GROUP:
                    return color.$7;
                  case NodeType.COUPON:
                  case NodeType.MESSAGE:
                    return color.$1;
                  case NodeType.TRIGGER:
                    return color.$16;
                  case NodeType.INBOX:
                  case NodeType.TAGGING:
                  case NodeType.MA:
                    return color.$23;
                  case NodeType.SCHEDULE:
                  case NodeType.WAIT:
                    return color.$24;
                  case NodeType.START:
                  default:
                    return color.$3;
                }
              }}
              nodeBorderRadius={12}
              nodeStrokeWidth={1}
              style={{
                bottom: 80,
                left: 15,
              }}
            />
            <UsageNum />
          </ReactFlow>
          {isPreview ? <Preview /> : <SettingBar />}
        </ReactFlowProvider>
      )}
    </div>
  );
};

export default mainContainer;
